import {
    withHeaders,
    instance as axiosInstance,
} from '../core/util/axiosInstance';
import { getDefaultHeaders } from '../core/util/other';
import { MutationFunction, UseMutationResult, useMutation } from 'react-query';
import { AxiosResponse } from 'axios';
import { commonShowAlert } from '../lib';
import { useAlert } from '../core';

interface CreateUserWalletProps {
    fullName: string;
    email: string;
    token: string;
}
interface UpdateUserWalletProps {
    fullName: string;
    email: string;
    id: string;
    token: string;
}
interface DeleteUserWalletProps {
    id: string;
    token: string;
}

export const createUserWallet: MutationFunction<
    AxiosResponse<any>,
    CreateUserWalletProps
> = async (props: CreateUserWalletProps) => {
    const { fullName, email, token } = props;
    const headers = getDefaultHeaders(token);
    const url = '/metadata/user/wallet';

    return axiosInstance().post(
        url,
        {
            fullName,
            email,
        },
        withHeaders(headers)
    );
};

export const updateUserWallet: MutationFunction<
    AxiosResponse<any>,
    UpdateUserWalletProps
> = async (props: UpdateUserWalletProps) => {
    const { fullName, email, token, id } = props;
    const headers = getDefaultHeaders(token);
    const url = `/metadata/user/wallet/${id}`;

    return axiosInstance().put(
        url,
        {
            fullName,
            email,
        },
        withHeaders(headers)
    );
};

export const deleteUserWallet: MutationFunction<
    AxiosResponse<any>,
    DeleteUserWalletProps
> = async (props: DeleteUserWalletProps) => {
    const { token, id } = props;
    const headers = getDefaultHeaders(token);
    const url = `/metadata/user/wallet/${id}`;

    return axiosInstance().delete(url, withHeaders(headers));
};

export const useUserWallet = (): UseMutationResult<
    AxiosResponse<any>,
    unknown,
    CreateUserWalletProps
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(createUserWallet, {
        onSuccess: () => {
            commonShowAlert(showAlert, hideAlert, 'success', 'Wallet Created');
        },
        onError: (error: any) => {
            // default error message.
            let errorMessage: string =
                error?.data?.message ?? 'Unable to create user wallet';

            // duplicated user wallet error
            if (
                error?.data?.error === 'CONFLICT' &&
                error?.data?.status === 409
            ) {
                errorMessage = 'User wallet already exists.';
            }
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};

export const useUpdateUserWallet = (): UseMutationResult<
    AxiosResponse<any>,
    unknown,
    UpdateUserWalletProps
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(updateUserWallet, {
        onSuccess: () => {
            commonShowAlert(showAlert, hideAlert, 'success', 'Wallet Updated');
        },
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ?? 'Unable to update user wallet';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};

export const useDeleteUserWallet = (): UseMutationResult<
    AxiosResponse<any>,
    unknown,
    DeleteUserWalletProps
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(deleteUserWallet, {
        onSuccess: () => {
            commonShowAlert(showAlert, hideAlert, 'success', 'Wallet Deleted');
        },
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ?? 'Unable to delete user wallet';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};
